import React, { memo, useCallback } from "react";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { EachProductStatus } from "./EachProductStatus";
import { EditableNumberInput } from "./EditableNumberInput";
import { getDateFromISOOne } from "../../../shared/utils";
import DropDn from "../../../assets/icons/drop.png";
import { Product } from "../../../types/ProductTypes";

interface TableRowProps {
  eachItem: Product;
  index: number;
  isSelected: boolean;
  handleCheckboxChange: (id: string) => void;
  handleInputChange: (itemId: string, field: string, value: string) => void;
  handleDeleteStoreProduct: (id: string) => void;
  handleActiveChange: (itemId: string, isActive: boolean) => void;
  pageProps: {
    pageNumber: number;
    pageSize: number;
    toggleProductRequest: {
      is_request_processing: boolean;
    };
  };
  productListReturnTo: string;
  activeInputId: string | null;
  setActiveInputId: (id: string | null) => void;
  handleMoveToTrash: (id: string) => void;
  handlePermanentDelete: (id: string) => void;
  handleReactivate: (id: string) => void;
  activeStatus: string;
}

export const TableRow = memo(
  ({
    eachItem,
    index,
    isSelected,
    handleCheckboxChange,
    handleInputChange,
    handleActiveChange,
    pageProps,
    productListReturnTo,
    activeInputId,
    setActiveInputId,
    handleDeleteStoreProduct,
    handleMoveToTrash,
    handlePermanentDelete,
    handleReactivate,
    activeStatus,
  }: TableRowProps) => {
    // const handleDeleteClick = useCallback(() => {
    //   handleDeleteStoreProduct(eachItem.id);
    // }, [handleDeleteStoreProduct, eachItem.id]);

    const handleReactivateClick = useCallback(() => {
      handleReactivate(eachItem.id);
    }, [handleReactivate, eachItem.id]);

    const handleMoveToTrashClick = useCallback(() => {
      handleMoveToTrash(eachItem.id);
    }, [handleMoveToTrash, eachItem.id]);

    const handlePermanentDeleteClick = useCallback(() => {
      handlePermanentDelete(eachItem.id);
    }, [handlePermanentDelete, eachItem.id]);

    return (
      <tr key={eachItem.id}>
        <td>
          <div className="custom-checkbox">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleCheckboxChange(eachItem.id)}
            />
          </div>
        </td>
        <td>
          <div className="product-info">
            <img src={eachItem?.urls?.[0] ?? ""} alt="" />
            <div className="prod-name">
              {eachItem?.name ?? "N/A"} <br />
              {/* <button onClick={handleDeleteClick} className='single-delete'>
                            Delete permenently
                        </button> */}
            </div>
          </div>
        </td>
        <td>
          <span className="stock">
            <EditableNumberInput
              initialValue={eachItem?.availableQuantity ?? 0}
              onChange={handleInputChange}
              itemId={eachItem.id}
              field="availableQuantity"
              isEditable={isSelected && activeStatus !== "Trash"}
              activeInputId={activeInputId}
              setActiveInputId={setActiveInputId}
            />
          </span>
        </td>
        <td>
          <span className="unit-price">
            <EditableNumberInput
              initialValue={eachItem?.newPrice ?? 0}
              // initialValue={eachItem?.price?.amount ?? 0}

              onChange={handleInputChange}
              itemId={eachItem.id}
              field="newPrice"
              isEditable={isSelected && activeStatus !== "Trash"}
              activeInputId={activeInputId}
              setActiveInputId={setActiveInputId}
            />
          </span>
        </td>
        <td>
          {eachItem?.categories?.map((eachCat: any, idx: any) => (
            <span className="cate" key={idx}>
              {eachCat}
            </span>
          ))}
          {(!eachItem?.categories || eachItem.categories.length === 0) ? "N/A" : ""}
        </td>
        <td>
          <div className="date-item">
            {getDateFromISOOne({ date: eachItem?.createdDate }) || "N/A"}
          </div>
        </td>
        <td>
          {eachItem?.ratingSummary && eachItem?.ratingSummary.count >= 1 && (
            <>
              {eachItem?.ratingSummary.averageRating}{" "}
              <span className="unit-price">
                ({eachItem.ratingSummary.count})
              </span>
            </>
          )}
          {eachItem?.ratingSummary && eachItem?.ratingSummary.count === 0
            ? "N/A"
            : ""}
        </td>
        <td>
          <div className="product-actions">
            <div className="is-active">
              <EachProductStatus
                eachItem={eachItem}
                pageProps={pageProps}
                isSelected={isSelected}
                handleActiveChange={handleActiveChange}
              />
            </div>
            <Dropdown className="report_dropdown" as={ButtonGroup}>
              <Dropdown.Toggle id="dropdown-basic" className="action-cta">
                <img src={DropDn} alt="" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {/* Show "Edit" if not in Trash tab */}
                {activeStatus !== "Trash" && (
                  <Dropdown.Item
                    href={`/app/my-products/${eachItem.id}/edit?returnTo=${encodeURIComponent(
                      productListReturnTo
                    )}`}
                  >
                    Edit
                  </Dropdown.Item>
                )}
                {/* Show "Move to Trash" if not in Trash tab */}
                {activeStatus !== "Trash" && (
                  <Dropdown.Item onClick={handleMoveToTrashClick}>
                    Move to Trash
                  </Dropdown.Item>
                )}

                {/* Reactivate from Trash */}
                {activeStatus === "Trash" && (
                  <Dropdown.Item onClick={handleReactivateClick}>
                    Restore
                  </Dropdown.Item>
                )}

                {/* Show "Delete Permanently" if in Trash tab */}
                {activeStatus === "Trash" && (
                  <Dropdown.Item onClick={handlePermanentDeleteClick}>
                    Delete Permanently
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </td>
      </tr>
    );
  },
  (prevProps, nextProps) => {
    const prevItem = prevProps.eachItem;
    const nextItem = nextProps.eachItem;
    return (
      prevItem.id === nextItem.id &&
      prevItem.availableQuantity === nextItem.availableQuantity &&
      prevItem.newPrice === nextItem.newPrice &&
      // prevItem.price?.amount === nextItem.price?.amount &&

      prevItem.isActive === nextItem.isActive &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.productListReturnTo === nextProps.productListReturnTo &&
      prevProps.activeInputId === nextProps.activeInputId &&
      prevProps.activeStatus === nextProps.activeStatus
    );
  }
);
